import { RegistrationConfirmation } from './types/details'

/**
 * The one place that decides which blocking modal a freshly loaded session gets.
 *
 * Both orchestrators — the imperative chain in `updateSubscriptionDetails` (details.tsx) and the
 * declarative safety net `OnboardingGuard` — used to carry their own copy of this `if / else if`
 * chain, and the copies had already drifted apart. Keep the chain here, pure and tested, and let
 * the orchestrators only translate the returned gate into a modal.
 *
 * The order matters: the first matching gate wins, exactly like the original chain.
 */
export type OnboardingGate =
  | 'confirmEmail'
  | 'onboarding'
  | 'trial'
  | 'appAdvice'
  | 'avv'
  | 'expired'
  | 'shopError'
  | 'paused'
  | null

export interface SubscriptionGateState {
  hasSubscriptions: boolean
  isMobileDevice: boolean
  isTaxConsultant: boolean
  overrideMobileWarning: boolean
  isAvvAccepted: boolean
  allSubscriptionsExpired: boolean
  allSubscriptionsCancelled: boolean
  readOnlyConfirmed: boolean
  hasLinkShopError: boolean
  isTrial: boolean
  allSubscriptionsLocked: boolean
  isLockedConfirmed: boolean
}

/**
 * DO-13065: `hasConfirmedEmail` stays raw (`false` for website-configurator users, whose address
 * was never verified), so it alone no longer decides whether the DOI modal blocks the session.
 * The backend derives `emailConfirmationDeferred` — including the 7-day grace period — so the
 * frontend needs no time logic: the flag flips to `false` on its own on day 8 and the modal returns.
 */
export const needsDoiPrompt = (confirmation?: Partial<RegistrationConfirmation> | null): boolean => {
  const { hasConfirmedEmail, emailConfirmationDeferred } = confirmation ?? {}
  return !hasConfirmedEmail && !emailConfirmationDeferred
}

/**
 * @param confirmation  `GET /user/confirmed` payload (may be empty or null while loading — treated as unconfirmed,
 *                      like the old `confirmation || {}` did)
 * @param subscription  optional — without it only the account gates (`confirmEmail`, `onboarding`)
 *                      are evaluated, which is all the `OnboardingGuard` safety net cares about
 */
export const getOnboardingGate = (
  confirmation?: Partial<RegistrationConfirmation> | null,
  subscription?: SubscriptionGateState,
): OnboardingGate => {
  const { onboardingFinished } = confirmation ?? {}

  if (needsDoiPrompt(confirmation)) return 'confirmEmail'
  // password + AVV live INSIDE the onboarding modal — this gate must stay reachable for
  // configurator users, i.e. it must not depend on hasConfirmedEmail
  if (!onboardingFinished) return 'onboarding'
  if (!subscription) return null

  const {
    hasSubscriptions,
    isMobileDevice,
    isTaxConsultant,
    overrideMobileWarning,
    isAvvAccepted,
    allSubscriptionsExpired,
    allSubscriptionsCancelled,
    readOnlyConfirmed,
    hasLinkShopError,
    isTrial,
    allSubscriptionsLocked,
    isLockedConfirmed,
  } = subscription

  if (!hasSubscriptions) return 'trial'
  if (isMobileDevice && !isTaxConsultant && !overrideMobileWarning) return 'appAdvice'
  if (!isAvvAccepted) return 'avv'
  if (allSubscriptionsExpired && allSubscriptionsCancelled && !readOnlyConfirmed) return 'expired'
  if (hasLinkShopError) return 'shopError'
  if (!isTrial && allSubscriptionsLocked && !isLockedConfirmed) return 'paused'

  return null
}
